import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { FileMetadata, GetFileResult, ListFilesResult } from "../../lib/types";

/** Unwrap an MCP tool result, surfacing the server's error text on failure. */
function unwrap<T>(r: { isError?: boolean; structuredContent?: T; content?: Array<{ text?: string }> }): T {
  if (r.isError) throw new Error(r.content?.[0]?.text ?? "Request failed");
  return r.structuredContent as T;
}

/**
 * Files attached to a node (Sprint 5n). Files are scoped by `entity_ref`; the
 * cockpit uses the node id as the ref, so uploads and listings line up 1:1.
 * Polls at 30s for the live-update cadence. Sprint 14g — `includeArchived`
 * toggles whether soft-archived files are returned (for the "Show archived"
 * chip).
 */
export function useListFiles(entityRef: string | undefined, includeArchived?: boolean) {
  return useQuery<ListFilesResult>({
    queryKey: entityRef ? [...queryKeys.files(entityRef), includeArchived ?? false] : ["files", ""],
    queryFn: () => {
      if (!entityRef) return { files: [], count: 0, limit: 0, offset: 0 };
      return mcpClient.listFiles({ entity_ref: entityRef, include_archived: includeArchived });
    },
    enabled: !!entityRef,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Sprint 14g — archive / restore / delete a file. Mirrors the routine hooks:
 * archive and restore are member-grade soft toggles; delete is the owner-only
 * hard delete (`force` overrides back-references). All invalidate the files
 * cache by prefix so every `entity_ref`/`includeArchived` variant refetches.
 */
export function useArchiveFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const r = await mcpClient.toolsCall("archive_file", { id });
      unwrap(r);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
  });
}

export function useRestoreFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const r = await mcpClient.toolsCall("restore_file", { id });
      unwrap(r);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
  });
}

export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }): Promise<void> => {
      const r = await mcpClient.toolsCall("delete_file", { id, ...(force ? { force: true } : {}) });
      unwrap(r);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
  });
}

/** Read a browser File into the shape upload_file expects (utf-8 or base64). */
function readFile(file: File): Promise<{ content: string; encoding: "utf-8" | "base64" }> {
  const isText = file.type.startsWith("text/") || /\.(md|markdown|txt|csv|json|ya?ml)$/i.test(file.name);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read_failed"));
    if (isText) {
      reader.onload = () => resolve({ content: String(reader.result ?? ""), encoding: "utf-8" });
      reader.readAsText(file);
    } else {
      reader.onload = () => {
        // readAsDataURL gives "data:<mime>;base64,<data>" — uploadFile strips the prefix.
        resolve({ content: String(reader.result ?? ""), encoding: "base64" });
      };
      reader.readAsDataURL(file);
    }
  });
}

export function useUploadFile() {
  const qc = useQueryClient();
  return useMutation<FileMetadata, Error, { file: File; entityRef: string }>({
    mutationFn: async ({ file, entityRef }) => {
      const { content, encoding } = await readFile(file);
      return mcpClient.uploadFile({
        filename: file.name,
        content,
        encoding,
        mime_type: file.type || undefined,
        entity_ref: entityRef,
      });
    },
    onSuccess: (_file, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.files(vars.entityRef) });
    },
  });
}

/** Imperative fetch for preview/download — get_file returns inline or a URL. */
export function fetchFile(id: string, mode?: "auto" | "url" | "inline"): Promise<GetFileResult> {
  return mcpClient.getFile(id, mode);
}
